import { CABIN_CLASS } from '@/helper/constants/aa-checkout';

/**
 * FlatBed Bundle Merge
 * Merge Economy bundles (VP, PF...) with Business Premium Flatbed bundle
 */

// Economy data cache
let economyCacheDepart: any = null;
let economyCacheReturn: any = null;

function simpleClone(obj: any): any {
  if (!obj) return obj;
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Get Premium Flatbed bundle from Business segment data
 * Business data has only one item: Premium Flatbed (PM)
 */
function getBusinessBundle(businessSegmentData: any): any {
  return businessSegmentData.ancillarylist[0];
}

/**
 * Cache Economy bundle data
 */
export function cacheEconomyData(tripsInfo: TripsInfo, response: any): void {
  const bundleData = response?.ancillary?.bundle;
  if (!bundleData) {
    return;
  }

  if (tripsInfo.departCabinClass === CABIN_CLASS.M) {
    // Cache only bundle data
    economyCacheDepart = simpleClone(bundleData);
  }

  if (tripsInfo.returnCabinClass === CABIN_CLASS.M) {
    // Cache only bundle data
    economyCacheReturn = simpleClone(bundleData);
  }
}

function isNeedsDepartMerge(departCabinClass: string): boolean {
  return departCabinClass === CABIN_CLASS.C && economyCacheDepart;
}

function isNeedsReturnMerge(returnCabinClass: string): boolean {
  return returnCabinClass === CABIN_CLASS.C && economyCacheReturn;
}

/**
 * Check if merge is needed (Business data needs to merge with Economy cache)
 */
export function needsMerge(tripsInfo: TripsInfo): boolean {
  const needsDepartMerge = isNeedsDepartMerge(tripsInfo.departCabinClass);
  const needsReturnMerge = isNeedsReturnMerge(tripsInfo.returnCabinClass);

  return needsDepartMerge || needsReturnMerge;
}

/**
 * Merge Business data with Economy cache
 */
export function mergeWithEconomyCache(
  response: any,
  tripsInfo: TripsInfo,
): any {
  const mergedResponse = simpleClone(response);

  // Merge depart data if Business cabin
  if (isNeedsDepartMerge(tripsInfo.departCabinClass)) {
    mergeSegment(
      mergedResponse,
      economyCacheDepart,
      response.ancillary.bundle,
      'depart',
    );
  }

  // Merge return data if Business cabin
  if (isNeedsReturnMerge(tripsInfo.returnCabinClass)) {
    mergeSegment(
      mergedResponse,
      economyCacheReturn,
      response.ancillary.bundle,
      'return',
    );
  }

  return mergedResponse;
}

function findBundleIndexByBundleId(
  bundleList: any[],
  bundleId: string,
): number {
  return bundleList?.findIndex((bundle: any) => bundle.bundleId === bundleId);
}

/**
 * Create merged segment from Economy data
 * Replace items with same bundleId using Business data
 */
function createMergedSegment(
  economySegmentData: any,
  businessBundle: any,
): any {
  const economyIndex = findBundleIndexByBundleId(
    economySegmentData?.ancillarylist,
    businessBundle.bundleId,
  );

  economySegmentData.ancillarylist[economyIndex] = businessBundle;

  return economySegmentData;
}

/**
 * Create merged metaData from Economy data
 * Replace items with same bundleId using Business metaData
 */
function createMergedMetaData(
  economyMetaData: any,
  businessBundleData: any,
  businessBundleId: string,
): any {
  const mergedMetaData = simpleClone(economyMetaData);
  const metaTypes = ['bundleCard', 'compareBundle'];

  metaTypes.forEach((metaType) => {
    const businessMetaItem = businessBundleData.metaData[metaType]?.find(
      (metaItem: any) => metaItem.bundleId === businessBundleId,
    );

    const economyMetaIndex = findBundleIndexByBundleId(
      mergedMetaData[metaType],
      businessBundleId,
    );

    mergedMetaData[metaType][economyMetaIndex] = businessMetaItem;
  });

  return mergedMetaData;
}

/**
 * Merge Economy and Business bundle data for single segment
 */
function mergeSegment(
  mergedResponse: any,
  economySegmentCache: any,
  businessBundleData: any,
  segmentType: 'depart' | 'return',
): void {
  const dataIndex = segmentType === 'depart' ? 0 : 1;
  const economyCache = simpleClone(economySegmentCache);

  // Get Business segment data
  const businessSegmentData = businessBundleData.data[dataIndex];

  // Get Business Premium Flatbed bundle
  const businessBundle = getBusinessBundle(businessSegmentData);

  // Merge segment data
  const economySegmentData = economyCache.data[dataIndex];
  const mergedSegment = createMergedSegment(economySegmentData, businessBundle);
  mergedResponse.ancillary.bundle.data[dataIndex] = mergedSegment;

  // Merge metaData
  const economyMetaData = economyCache.metaData;
  const mergedMetaData = createMergedMetaData(
    economyMetaData,
    businessBundleData,
    businessBundle.bundleId,
  );
  mergedResponse.ancillary.bundle.metaData = mergedMetaData;
}

/**
 * Restore selected bundle
 */
export function restoreSelectedBundleWithNewData(
  bundleData: any[],
  savedSelection: any,
): any {
  if (!bundleData || !Array.isArray(bundleData)) {
    return savedSelection;
  }

  const savedDepartBundleId = savedSelection?.departFlight?.bundleId;
  const savedReturnBundleId = savedSelection?.returnFlight?.bundleId;

  // Find bundle by bundleId
  const newDepartFlight = bundleData.find(
    (b) => b.departFlight?.bundleId === savedDepartBundleId,
  )?.departFlight;

  const newReturnFlight = bundleData.find(
    (b) => b.returnFlight?.bundleId === savedReturnBundleId,
  )?.returnFlight;

  const result = {
    departFlight: newDepartFlight,
    returnFlight: newReturnFlight,
  };

  return result;
}
